import { removeMoneyMask, ToggleInputTypes } from '@avantstay/backoffice-core'

import { DiscountBulkUpdateTypes } from './types/api/discountTypes'
import { MarkupGridFormattedItem, MarkupGridItem } from './types/api/markups.types'
import { BulkUpdatePayload, RateUpdateTypes } from './types/api/pricingTapeChart.types'

const calculateNewValue = (
  previousValue: number,
  valueToEdit: string,
  type: ToggleInputTypes,
): number => {
  const valueToEditToNumber = Number(valueToEdit)

  if (type === ToggleInputTypes.money) {
    return previousValue + Number((100 * valueToEditToNumber).toFixed(2))
  }

  if (type === ToggleInputTypes.percentage) {
    return Math.round(
      Number((previousValue + (valueToEditToNumber / 100) * previousValue).toFixed(2)),
    )
  }
  return previousValue
}

const formatNewFixedPrice = (newValue: string): number => {
  const value = removeMoneyMask(newValue)
  return Number((100 * Number(value)).toFixed(2))
}

export const getNewRate = (rate: number, payload: BulkUpdatePayload, floorRate: number) => {
  if (payload.updateType === RateUpdateTypes.asFloorRate) {
    return floorRate
  }

  return payload.updateType === RateUpdateTypes.fixed
    ? formatNewFixedPrice(payload.rateToUpdate as string)
    : calculateNewValue(
        rate as number,
        payload.rateToUpdate as string,
        payload.kindOfRateUpdate as ToggleInputTypes,
      )
}

export const getSeasonUpdateErrorMessage = (errorMessage: string): string => {
  if (errorMessage.includes('cannot have overlapping periods of the same Season Type'))
    return 'Unable to have overlapping periods of the same Season Type in the same Season Group'

  return errorMessage
}

export const formatMarkups = (markups: MarkupGridItem[]): MarkupGridFormattedItem[] => {
  return Object.values(
    markups.reduce(
      (acc, markup) => {
        const { seasonGroupId, seasonGroupName, value, revenueTarget, author, updatedAt } = markup

        const newMarkup = { value, revenueTarget }
        const existingMarkupGroup = acc[seasonGroupId]

        if (!existingMarkupGroup) {
          const newMarkupGroup = {
            seasonGroupId,
            seasonGroupName,
            author,
            updatedAt,
            markups: [newMarkup],
            [revenueTarget]: value,
          }
          return { ...acc, [seasonGroupId]: newMarkupGroup }
        }

        const updatedMarkups = [...existingMarkupGroup.markups, newMarkup]
        const updatedAgencyValues = { ...existingMarkupGroup, [revenueTarget]: value }
        const updatedMarkupGroup = {
          ...existingMarkupGroup,
          ...updatedAgencyValues,
          markups: updatedMarkups,
        }

        return { ...acc, [seasonGroupId]: updatedMarkupGroup }
      },
      {} as { [seasonGroupId: string]: MarkupGridFormattedItem },
    ),
  )
}

export const getNewDiscountValue = (
  editType: DiscountBulkUpdateTypes,
  previousValue: number,
  newValue?: number,
): number => {
  if (!newValue === undefined) {
    return previousValue
  }
  if (editType === DiscountBulkUpdateTypes.fixed) {
    return newValue as number
  }
  if (editType === DiscountBulkUpdateTypes.increase && newValue) {
    const incAmount = (previousValue * (newValue as number)) / 100
    return previousValue + incAmount
  }
  if (editType === DiscountBulkUpdateTypes.decrease && newValue) {
    const decAmount = (previousValue * (newValue as number)) / 100
    return Math.max(0, previousValue - decAmount)
  }
  return previousValue
}

export const replaceDiscountValue = (discounts: number[], newValue: number, index: number) => {
  if (index >= 0 && index < discounts.length) {
    return [...discounts.slice(0, index), newValue, ...discounts.slice(index + 1)]
  }
  return discounts
}

export const parsePeriodDiscounts = (
  previousDiscounts: number[],
  newDiscounts?: Array<number | undefined>,
): number[] => {
  if (!newDiscounts) return previousDiscounts

  return newDiscounts.map((discount, index) => {
    if (typeof discount === 'undefined') {
      return previousDiscounts[index]
    }
    return discount
  })
}

export const deleteItemByIndex = (array: Array<number | undefined> | undefined, index: number) => {
  if (
    array === undefined ||
    index < 0 ||
    index >= array.length ||
    typeof array[index] === 'undefined'
  ) {
    return []
  }

  const resultArray = [...array.slice(0, index), ...array.slice(index + 1)]
  return resultArray
}

export const noDiscountsToUpdate = (array: (number | undefined)[] | undefined): boolean => {
  if (!array) return true
  return array.every(item => item === undefined)
}
